import { useMemo } from "react";

import { Button, CloseSVG, LoadingSpinner, Modal } from "@newsela/angelou";

import {
  actionNames,
  objectTypes,
  eventTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { useConceptTaxonomyProvider } from "@/search/Langston/ConceptTaxonomyProvider";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import {
  ConceptNode,
  IModalProps,
  SubjectToConceptData,
  SubjectKey,
} from "../Concepts.interface";
import { RecursiveConceptTree } from "./RecursiveConceptTree";
import { SearchTags } from "./SearchTags";
import { TaxonomyModalTabs } from "./TaxonomyModalTabs";
import { TaxonomyModalFooter } from "./TaxonomyModalFooter";
import { useConceptTaxonomyModal } from "./useConceptTaxonomyModal";

import {
  closeButtonCss,
  headerTitleCss,
  modalContainerCss,
  modalContentCss,
  modalHeaderCss,
} from "./ConceptTaxonomyModal.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

const closeModalEventProperties = {
  objectType: objectTypes.user,
  actionName: actionNames.update,
  event_type: eventTypes.click,
  streamProperties: {
    component_type: "TaxonomyCloseModalButton",
    description: "Close Taxonomy Modal",
  },
};

interface CreateTabsProps {
  filteredTree: SubjectToConceptData;
  isLoadingTaxonomyData: boolean;
  handleToggle: (id: string, checked: boolean) => void;
  draftSelectedIds: Set<string>;
  handleIsNodeChecked: (id: string) => boolean;
  subjectTabMapping: { panelId: SubjectKey; name: string }[];
}

export const createTabs = ({
  filteredTree,
  isLoadingTaxonomyData,
  handleToggle,
  draftSelectedIds,
  handleIsNodeChecked,
  subjectTabMapping,
}: CreateTabsProps) => {
  const allSubjectTreesCombined = Object.values(filteredTree).flat();

  const allTabsContent = isLoadingTaxonomyData ? (
    <LoadingSpinner />
  ) : (
    <>
      {allSubjectTreesCombined.map((nodeTree) => (
        <RecursiveConceptTree
          key={nodeTree?.id}
          node={nodeTree as ConceptNode}
          selectedIds={draftSelectedIds}
          onToggle={handleToggle}
          isNodeSelected={handleIsNodeChecked}
        />
      ))}
    </>
  );
  return [
    {
      name: "All",
      panelId: "all",
      panelContent: allTabsContent,
    },
    ...(!isLoadingTaxonomyData
      ? subjectTabMapping.map((tabItem) => {
          const conceptTreeData = filteredTree[tabItem.panelId];

          return {
            ...tabItem,
            panelContent: !conceptTreeData ? (
              <span>No Taxonomy tags were found.</span>
            ) : (
              <RecursiveConceptTree
                node={conceptTreeData}
                selectedIds={draftSelectedIds}
                onToggle={handleToggle}
                isNodeSelected={handleIsNodeChecked}
              />
            ),
          };
        })
      : []),
  ];
};

export const ConceptTaxonomyModal = ({
  modalProps,
  setActive,
}: TaxonomyTagsProps) => {
  const {
    getters: { isLoadingTaxonomyData },
  } = useSearchPageContext();
  const { isTabletOrMobileView } = useConceptTaxonomyProvider();

  const {
    draftSelectedIds,
    filteredTree,
    handleIsNodeChecked,
    handleSearch,
    handleSubmit,
    handleToggle,
    subjectTabMapping,
  } = useConceptTaxonomyModal({ setActive });

  const panelData = useMemo(() => {
    return createTabs({
      filteredTree,
      isLoadingTaxonomyData,
      handleToggle,
      draftSelectedIds,
      handleIsNodeChecked,
      subjectTabMapping,
    });
  }, [JSON.stringify(filteredTree), draftSelectedIds]);

  return (
    <Modal
      {...modalProps}
      labelId="modal_title"
      ariaProps={{
        "aria-labelledby": "modal_title",
        "aria-describedby": "modal_description",
      }}
      dataProps={{
        "data-testid": "concept-taxonomy-tags-modal",
      }}
      __cssFor={{ root: modalContainerCss }}
    >
      <div css={modalContentCss}>
        <div css={modalHeaderCss}>
          <h1 css={headerTitleCss} id="modal_title">
            Add topic to filters
          </h1>
          {/* @ts-expect-error Property 'children' does not exist on type */}
          <ButtonWithTrackEvent
            {...closeModalEventProperties}
            __cssFor={{ root: closeButtonCss }}
            flavor={Button.flavors.incognito}
            ariaProps={{
              "aria-label": "Close Modal Button",
            }}
            icon={{ SvgComponent: CloseSVG }}
            onClick={() => setActive(false)}
            dataProps={{
              "data-qa-selector": "close-taxonomy-modal",
              "data-testid": "close-modal-button",
            }}
          />
        </div>
        {isTabletOrMobileView && <SearchTags onSearch={handleSearch} />}
        <TaxonomyModalTabs panelData={panelData} handleSearch={handleSearch} />
        <TaxonomyModalFooter
          onSubmit={handleSubmit}
          onCancel={() => setActive(false)}
        />
      </div>
    </Modal>
  );
};

interface TaxonomyTagsProps {
  modalProps: IModalProps;
  setActive: (isActive: boolean) => void;
}
